// PrivacyPolicy.tsx
import React from "react";
import "./css/PrivacyPolicy.css";

export const PrivacyPolicy: React.FC = () => {
    return (
        <div className="privacy-policy">
            <div className="privacy-policy__container">
                <h1 className="privacy-policy__title">Skitterbook Privacy Policy</h1>
                <p className="privacy-policy__last-updated">
                    Last updated: {new Date().toLocaleDateString("en-GB")}
                </p>

                <section className="privacy-policy__section">
                    <h2>1. Who we are</h2>
                    <p>
                        Skitterbook is operated by FAR Authoring Ltd.{" "}
                        (referred to in this policy as “Skitterbook”, “we”, “us” or “our”).
                    </p>
                    <p>
                        <strong>Legal entity:</strong> FAR Authoring Ltd.
                        <br />
                        <strong>Registered address:</strong> 41 Grange Park, Ealing, London,
                        W5 3PR, United Kingdom
                        <br />
                        <strong>Contact email:</strong>{" "}
                        <a href="mailto:farauthoring@gmail.com">
                            farauthoring@gmail.com
                        </a>
                    </p>
                    <p>
                        This Privacy Policy explains how we collect, use, store and protect
                        your information when you use the Skitterbook web application
                        hosted on Microsoft Azure (the “Service”).
                    </p>
                </section>

                <section className="privacy-policy__section">
                    <h2>2. Scope and legal basis</h2>
                    <p>
                        This Privacy Policy applies to all users of the Skitterbook web
                        application worldwide. As we are based in the United Kingdom, we
                        process personal data in accordance with applicable data protection
                        laws, including the UK General Data Protection Regulation (UK GDPR)
                        and the Data Protection Act 2018, and, where relevant, the EU
                        General Data Protection Regulation (EU GDPR).
                    </p>
                    <p>
                        We only process your personal data where we have a valid legal
                        basis, which may include:
                    </p>
                    <ul>
                        <li>
                            <strong>Contract:</strong> to provide you with access to and use
                            of Skitterbook.
                        </li>
                        <li>
                            <strong>Legitimate interests:</strong> to operate, secure and
                            improve the Service.
                        </li>
                        <li>
                            <strong>Consent:</strong> where required, for example for certain
                            cookies or optional communications.
                        </li>
                    </ul>
                </section>

                <section className="privacy-policy__section">
                    <h2>3. Information we collect</h2>
                    <p>We collect and process the following categories of information:</p>

                    <h3>3.1 Account information</h3>
                    <ul>
                        <li>
                            <strong>Given name:</strong> used to personalise your experience
                            and identify your account.
                        </li>
                        <li>
                            <strong>Email address:</strong> used for account access, service
                            communications and library storage of your flipbooks.
                        </li>
                        <li>
                            <strong>Password:</strong> used to secure your account. Passwords
                            are stored in a secure, hashed form and are not visible to us in
                            plain text.
                        </li>
                    </ul>

                    <h3>3.2 Content you upload</h3>
                    <ul>
                        <li>
                            <strong>Video uploads:</strong> short videos you upload so that
                            Skitterbook can generate flipbooks from extracted images.
                        </li>
                        <li>
                            <strong>Title page images:</strong> images you upload to be used
                            as title pages for your flipbooks.
                        </li>
                    </ul>
                    <p>
                        Uploaded videos and images are stored securely and are used only to
                        provide the Service to you. Uploaded images and videos are solely
                        the possession of Skitterbook users and cannot be viewed by other
                        users or by system managers in the ordinary operation of the
                        Service.
                    </p>

                    <h3>3.3 Usage and analytics data</h3>
                    <p>
                        We collect anonymous analytics and profile information to understand
                        how the Service is used and to improve performance and reliability.
                        This may include:
                    </p>
                    <ul>
                        <li>
                            <strong>Technical data:</strong> browser type, device type,
                            operating system, and similar technical information.
                        </li>
                        <li>
                            <strong>Usage data:</strong> pages visited, features used, timing
                            and frequency of use.
                        </li>
                    </ul>
                    <p>
                        This analytics data is retained and used for reference, product
                        improvement and diagnostic purposes and is stored in Microsoft Azure
                        Application Insights.
                    </p>
                </section>

                <section className="privacy-policy__section">
                    <h2>4. How we use your information</h2>
                    <p>We use your information for the following purposes:</p>
                    <ul>
                        <li>
                            <strong>To provide the Service:</strong> creating and managing
                            your account, generating flipbooks from your uploaded videos and
                            images, and storing your flipbooks in your client library.
                        </li>
                        <li>
                            <strong>To operate and improve Skitterbook:</strong> monitoring
                            performance, diagnosing issues, and enhancing features using
                            anonymous analytics data.
                        </li>
                        <li>
                            <strong>To communicate with you:</strong> sending essential
                            service messages about your account or important changes to the
                            Service or this Privacy Policy.
                        </li>
                        <li>
                            <strong>To ensure security:</strong> protecting the Service
                            against misuse, unauthorised access and other security risks.
                        </li>
                    </ul>
                </section>

                <section className="privacy-policy__section">
                    <h2>5. Cookies and similar technologies</h2>
                    <p>
                        Skitterbook uses cookies and similar technologies to support the
                        operation of the Service. Cookies are small text files stored on
                        your device.
                    </p>
                    <p>We use cookies for the following purposes:</p>
                    <ul>
                        <li>
                            <strong>Session management:</strong> to store session account
                            details and keep you signed in while you use the Service.
                        </li>
                        <li>
                            <strong>Application settings:</strong> to remember your
                            preferences and configuration within Skitterbook.
                        </li>
                        <li>
                            <strong>Performance and reliability:</strong> to enhance
                            performance and stability of the Service.
                        </li>
                    </ul>
                    <p>
                        You can control cookies through your browser settings. However, if
                        you disable certain cookies, some features of Skitterbook may not
                        function correctly.
                    </p>
                </section>

                <section className="privacy-policy__section">
                    <h2>6. Data sharing and third parties</h2>
                    <p>
                        In this version of the application, we do not use payment processors
                        and we do not integrate with third‑party services for the core
                        operation of Skitterbook.
                    </p>
                    <p>
                        Your data is processed and stored on Microsoft Azure infrastructure,
                        including Azure Application Insights for analytics. These providers
                        act as our data processors and process data on our behalf in
                        accordance with our instructions and applicable data protection
                        laws.
                    </p>
                    <p>
                        We do not sell your personal data and we do not share your personal
                        data with third parties for their own marketing purposes.
                    </p>
                </section>

                <section className="privacy-policy__section">
                    <h2>7. International access</h2>
                    <p>
                        Skitterbook is currently provided in English and is intended for a
                        global audience. Your data may be stored or processed in the United
                        Kingdom or other locations where our hosting providers operate. When
                        data is transferred outside the UK or European Economic Area, we
                        will take appropriate steps to ensure that an adequate level of
                        protection is in place, in line with applicable data protection
                        laws.
                    </p>
                </section>

                <section className="privacy-policy__section">
                    <h2>8. Children and age restrictions</h2>
                    <p>
                        Skitterbook does not include specific age restrictions and is not
                        targeted at children. If you are a parent or guardian and believe
                        that a child has provided us with personal data, please contact us
                        so that we can review and, where appropriate, remove that
                        information.
                    </p>
                </section>

                <section className="privacy-policy__section">
                    <h2>9. Data retention</h2>
                    <p>
                        We retain your personal data only for as long as necessary to fulfil
                        the purposes described in this Privacy Policy or as required by law.
                        This generally means:
                    </p>
                    <ul>
                        <li>
                            <strong>Account data:</strong> retained while your account is
                            active. If you delete your account, we will delete or anonymise
                            your personal data within a reasonable period, subject to any
                            legal obligations to retain certain records.
                        </li>
                        <li>
                            <strong>Uploaded content:</strong> retained while your account is
                            active so that your flipbooks and associated media remain
                            available in your library.
                        </li>
                        <li>
                            <strong>Analytics data:</strong> retained in Azure Application
                            Insights for product improvement and diagnostic purposes for a
                            period that is appropriate for trend analysis and system
                            reliability.
                        </li>
                    </ul>
                </section>

                <section className="privacy-policy__section">
                    <h2>10. Your rights</h2>
                    <p>
                        Depending on where you are located, you may have certain rights in
                        relation to your personal data, including:
                    </p>
                    <ul>
                        <li>
                            <strong>Access:</strong> to request a copy of the personal data we
                            hold about you.
                        </li>
                        <li>
                            <strong>Rectification:</strong> to request correction of inaccurate
                            or incomplete data.
                        </li>
                        <li>
                            <strong>Erasure:</strong> to request deletion of your personal
                            data in certain circumstances.
                        </li>
                        <li>
                            <strong>Restriction:</strong> to request that we limit the
                            processing of your data in certain circumstances.
                        </li>
                        <li>
                            <strong>Objection:</strong> to object to processing based on our
                            legitimate interests.
                        </li>
                        <li>
                            <strong>Portability:</strong> to request that we provide your data
                            in a structured, commonly used and machine‑readable format where
                            applicable.
                        </li>
                    </ul>
                    <p>
                        To exercise any of these rights, please contact us at{" "}
                        <a href="mailto:farauthoring@gmail.com">
                            farauthoring@gmail.com
                        </a>
                        . We may need to verify your identity before responding to your
                        request.
                    </p>
                </section>

                <section className="privacy-policy__section">
                    <h2>11. Security</h2>
                    <p>
                        We take appropriate technical and organisational measures to protect
                        your personal data against unauthorised access, loss, misuse or
                        alteration. This includes secure storage of uploaded videos and
                        images, secure password handling, and the use of reputable hosting
                        infrastructure on Microsoft Azure.
                    </p>
                    <p>
                        However, no method of transmission over the internet or method of
                        electronic storage is completely secure, and we cannot guarantee
                        absolute security.
                    </p>
                </section>

                <section className="privacy-policy__section">
                    <h2>12. Changes to this Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time to reflect
                        changes in the Service, legal requirements or how we process your
                        data. When we make material changes, we will take reasonable steps
                        to notify you, such as by displaying a notice within the Service or
                        updating the “Last updated” date at the top of this page.
                    </p>
                    <p>
                        Your continued use of Skitterbook after any changes have been made
                        will constitute your acceptance of the updated Privacy Policy.
                    </p>
                </section>

                <section className="privacy-policy__section">
                    <h2>13. Contact us</h2>
                    <p>
                        If you have any questions, concerns or requests regarding this
                        Privacy Policy or how we handle your personal data, please contact
                        us at:
                    </p>
                    <p>
                        <strong>Email:</strong>{" "}
                        <a href="mailto:farauthoring@gmail.com">
                            farauthoring@gmail.com
                        </a>
                        <br />
                        <strong>Address:</strong> FAR Authoring Ltd., 41 Grange Park,
                        Ealing, London, W5 3PR, United Kingdom
                    </p>
                </section>

                <section className="privacy-policy__section privacy-policy__disclaimer">
                    <p>
                        This Privacy Policy is provided for information purposes and does
                        not constitute legal advice. You may wish to seek independent legal
                        review to ensure that it fully meets your regulatory obligations.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
